import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/Badges";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { History, X, UserCheck } from "lucide-react";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { usePageTitle } from "@/hooks/usePageTitle";
import { fmtDateTime } from "@/lib/format";

export default function AdminHistory() {
  usePageTitle("Historial de aprobaciones · Gestión de clientes");
  const [data, setData] = useState<{ members: string[]; entries: any[] }>({ members: [], entries: [] });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // Filtros en la URL: se conservan al recargar y se pueden compartir.
  const [params, setParams] = useSearchParams();
  const from = params.get("from") || "";
  const to = params.get("to") || "";
  const by = params.get("by") || "all";

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v && v !== "all") next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  const load = () => {
    setLoading(true);
    setFailed(false);
    const q: any = {};
    if (from) q.from = from;
    if (to) q.to = to;
    if (by !== "all") q.by = by;
    api.get("/admin/approval-history", { params: q }).then((r) => setData(r.data)).catch(() => setFailed(true)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [from, to, by]); // eslint-disable-line

  const hasFilters = !!(from || to || by !== "all");

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-history-page">
      <div className="card-obsidian p-5">
        <div className="grid sm:grid-cols-4 gap-4 items-end">
          <div>
            <Label htmlFor="history-from" className="text-slate-700 text-sm">Desde</Label>
            <Input id="history-from" data-testid="history-from-input" type="date" value={from} onChange={(e: any) => setParam("from", e.target.value)} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
          </div>
          <div>
            <Label htmlFor="history-to" className="text-slate-700 text-sm">Hasta</Label>
            <Input id="history-to" data-testid="history-to-input" type="date" value={to} onChange={(e: any) => setParam("to", e.target.value)} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
          </div>
          <div>
            <Label htmlFor="history-member" className="text-slate-700 text-sm">Decidido por</Label>
            <Select value={by} onValueChange={(v: string) => setParam("by", v)}>
              <SelectTrigger id="history-member" data-testid="history-member-select" className="mt-1.5 bg-obsidian border-slate-200 text-slate-900"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-obsidian-card border-slate-200 text-slate-900">
                <SelectItem value="all">Cualquier persona</SelectItem>
                {data.members.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="history-clear-button" variant="outline" onClick={() => setParams({}, { replace: true })} disabled={!hasFilters} className="border-slate-200 text-slate-700 hover:bg-slate-100 rounded-full">
            <X className="w-4 h-4 mr-1" aria-hidden="true" /> Limpiar filtros
          </Button>
        </div>
      </div>

      {loading ? (
        <PageLoader />
      ) : failed ? (
        <LoadError what="el historial" onRetry={load} />
      ) : data.entries.length === 0 ? (
        <EmptyState icon={History}>
          {hasFilters ? "No hay decisiones para los filtros seleccionados." : "Todavía no se ha aprobado ni rechazado ningún registro."}
        </EmptyState>
      ) : (
        <div className="card-obsidian overflow-hidden">
          <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Tabla del historial de aprobaciones">
            <table className="w-full text-sm min-w-[680px]">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                  <th scope="col" className="text-left font-medium px-5 py-3">Cliente</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Decisión</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Por</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Fecha (hora de Ecuador)</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e, i) => (
                  <tr key={i} data-testid="history-row" className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <Link to={`/admin/usuarios/${e.user_id}`} className="text-slate-900 font-medium hover:underline">{e.user_name}</Link>
                      <p className="text-xs text-slate-600 font-mono">{e.cedula || "—"}</p>
                    </td>
                    <td className="px-5 py-3"><StatusBadge status={e.decision} /></td>
                    <td className="px-5 py-3 text-slate-700">
                      <span className="inline-flex items-center gap-1.5"><UserCheck className="w-3.5 h-3.5 text-gold-ink" aria-hidden="true" /> {e.by || "—"}</span>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{fmtDateTime(e.at)}</td>
                    <td className="px-5 py-3 text-slate-700 max-w-xs">{e.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
